import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { Task, TaskPriority } from "@/types";

interface TaskRow {
  id: string;
  workspace_id: string;
  project_id: string | null;
  conversation_id: string | null;
  title: string;
  notes: string;
  due_at: number | null;
  priority: TaskPriority;
  completed: number;
  completed_at: number | null;
  created_at: number;
  updated_at: number;
}

function rowToTask(row: TaskRow): Task {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    projectId: row.project_id,
    conversationId: row.conversation_id,
    title: row.title,
    notes: row.notes,
    dueAt: row.due_at,
    priority: row.priority,
    completed: row.completed === 1,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const taskRepository = {
  async list(workspaceId: string): Promise<Task[]> {
    const db = await getDb();
    const rows = await db.select<TaskRow[]>(
      "SELECT * FROM tasks WHERE workspace_id = $1 ORDER BY completed ASC, due_at ASC, created_at DESC",
      [workspaceId],
    );
    return rows.map(rowToTask);
  },

  async create(input: {
    workspaceId: string;
    title: string;
    notes?: string;
    dueAt?: number | null;
    priority?: TaskPriority;
    projectId?: string | null;
    conversationId?: string | null;
  }): Promise<Task> {
    const db = await getDb();
    const now = Date.now();
    const task: Task = {
      id: createId("task"),
      workspaceId: input.workspaceId,
      projectId: input.projectId ?? null,
      conversationId: input.conversationId ?? null,
      title: input.title,
      notes: input.notes ?? "",
      dueAt: input.dueAt ?? null,
      priority: input.priority ?? "normal",
      completed: false,
      completedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await db.execute(
      `INSERT INTO tasks (id, workspace_id, project_id, conversation_id, title, notes, due_at, priority, completed, completed_at, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, 0, NULL, $9, $10)`,
      [
        task.id,
        task.workspaceId,
        task.projectId,
        task.conversationId,
        task.title,
        task.notes,
        task.dueAt,
        task.priority,
        now,
        now,
      ],
    );
    return task;
  },

  async update(
    id: string,
    patch: { title?: string; notes?: string; dueAt?: number | null; priority?: TaskPriority; projectId?: string | null },
  ): Promise<void> {
    const db = await getDb();
    const rows = await db.select<TaskRow[]>("SELECT * FROM tasks WHERE id = $1", [id]);
    const existing = rows[0];
    if (!existing) return;
    await db.execute(
      "UPDATE tasks SET title = $1, notes = $2, due_at = $3, priority = $4, project_id = $5, updated_at = $6 WHERE id = $7",
      [
        patch.title ?? existing.title,
        patch.notes ?? existing.notes,
        patch.dueAt !== undefined ? patch.dueAt : existing.due_at,
        patch.priority ?? existing.priority,
        patch.projectId !== undefined ? patch.projectId : existing.project_id,
        Date.now(),
        id,
      ],
    );
  },

  async setCompleted(id: string, completed: boolean): Promise<void> {
    const db = await getDb();
    const now = Date.now();
    await db.execute("UPDATE tasks SET completed = $1, completed_at = $2, updated_at = $3 WHERE id = $4", [
      completed ? 1 : 0,
      completed ? now : null,
      now,
      id,
    ]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM tasks WHERE id = $1", [id]);
  },
};
